import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Clock, Save, Users } from 'lucide-react';
import { services as servicesApi, groomers as groomersApi } from '../utils/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const GroomerSpeedManager = () => {
  const [groomers, setGroomers] = useState([]);
  const [services, setServices] = useState([]);
  const [speeds, setSpeeds] = useState({});
  const [sizeCategories, setSizeCategories] = useState([]);
  const [sizingEnabled, setSizingEnabled] = useState(true);
  const [speedsEnabled, setSpeedsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  const loadData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      console.log('[GroomerSpeedManager] Starting to load data...');
      
      const groomersRes = await groomersApi.getAll();
      console.log('[GroomerSpeedManager] Groomers loaded:', groomersRes.data);
      
      const servicesRes = await servicesApi.getAll();
      console.log('[GroomerSpeedManager] Services loaded:', servicesRes.data);
      
      const speedsRes = await axios.get(`${API_URL}/groomer-speeds`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[GroomerSpeedManager] Speeds loaded:', speedsRes.data);
      
      const sizeCategoriesRes = await axios.get(`${API_URL}/size-categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[GroomerSpeedManager] Size categories loaded:', sizeCategoriesRes.data);
      
      const settingsRes = await axios.get(`${API_URL}/size-categories/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[GroomerSpeedManager] Settings loaded:', settingsRes.data);
      
      const speedsEnabledRes = await axios.get(`${API_URL}/business-info/groomer_speeds_enabled`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => ({ data: { info_value: 'true' } }));
      console.log('[GroomerSpeedManager] Speeds enabled:', speedsEnabledRes.data);

      // Groomers API returns groomer records, not user records, so no role filtering needed
      const groomersList = groomersRes.data;
      const servicesList = servicesRes.data;
      
      console.log('Groomers:', groomersList);
      console.log('Services:', servicesList);
      console.log('Groomers length:', groomersList.length);
      console.log('Services length:', servicesList.length);
      
      setGroomers(groomersList);
      setServices(servicesList);
      setSizeCategories(sizeCategoriesRes.data);
      setSizingEnabled(settingsRes.data.sizing_enabled === 'true');
      setSpeedsEnabled(speedsEnabledRes.data.info_value !== 'false');
      
      // Convert speeds array to nested object for easier access
      const speedsMap = {};
      speedsRes.data.forEach(speed => {
        if (!speedsMap[speed.groomer_id]) speedsMap[speed.groomer_id] = {};
        if (!speedsMap[speed.groomer_id][speed.service_id]) speedsMap[speed.groomer_id][speed.service_id] = {};
        // Convert string to float
        speedsMap[speed.groomer_id][speed.service_id][speed.size] = parseFloat(speed.duration_multiplier);
      });
      
      console.log('Loaded speeds from API:', speedsRes.data);
      console.log('Speeds map:', speedsMap);
      
      setSpeeds(speedsMap);
    } catch (error) {
      console.error('Load data error:', error);
      console.warn('Employee speeds not configured yet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    
    // Reload data when size categories are updated
    const handleSizeCategoryUpdate = () => {
      console.log('[GroomerSpeedManager] sizeCategoryUpdated event received, reloading...');
      loadData();
    };

    window.addEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    
    return () => {
      window.removeEventListener('sizeCategoryUpdated', handleSizeCategoryUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSpeedChange = (groomerId, serviceId, size, value) => {
    console.log('handleSpeedChange called:', { groomerId, serviceId, size, value });
    setSpeeds(prev => {
      const newSpeeds = {
        ...prev,
        [groomerId]: {
          ...prev[groomerId],
          [serviceId]: {
            ...prev[groomerId]?.[serviceId],
            [size]: parseFloat(value),
          },
        },
      };
      console.log('New speeds state:', newSpeeds);
      return newSpeeds;
    });
  };

  const getSpeed = (groomerId, serviceId, size) => {
    return speeds[groomerId]?.[serviceId]?.[size] || 1.00;
  };

  const handleToggleSpeeds = async () => {
    setSavingToggle(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/business-info/groomer_speeds_enabled`,
        { value: (!speedsEnabled).toString() },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setSpeedsEnabled(!speedsEnabled);
      toast.success(`Groomer service speeds ${!speedsEnabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Toggle speeds error:', error);
      toast.error('Failed to update speeds setting');
    } finally {
      setSavingToggle(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      
      // Build complete speeds array from ALL groomers and services
      const speedsArray = [];
      
      groomers.forEach(groomer => {
        services.forEach(service => {
          sizeCategories.forEach(sizeCategory => {
            const size = sizeCategory.category_name;
            const baseDuration = service.pricing?.find(p => p.size === size)?.durationMinutes;
            if (baseDuration && sizeCategory.is_active) {
              const multiplier = getSpeed(groomer.id, service.id, size);
              speedsArray.push({
                groomerId: groomer.id,
                serviceId: service.id,
                size,
                durationMultiplier: parseFloat(multiplier),
              });
            }
          });
        });
      });

      console.log('Current speeds state:', speeds);
      console.log('Saving speeds array:', speedsArray);
      console.log('Number of speeds to save:', speedsArray.length);

      const response = await axios.put(
        `${API_URL}/groomer-speeds`,
        { speeds: speedsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Save response:', response.data);
      toast.success('Employee speeds saved successfully!');
      
      // Reload data to ensure UI matches database
      await loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save employee speeds');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-4">
        <Clock className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Employee Service Speeds</h2>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Enable Employee Service Speeds</h3>
            <p className="text-sm text-gray-600">
              When enabled, each employee can have custom service durations based on their speed.
              When disabled, all employees use the standard service durations defined in the services settings.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={speedsEnabled}
              onChange={handleToggleSpeeds}
              disabled={savingToggle}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
          </label>
        </div>
      </div>

      {!speedsEnabled ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Clock className="mx-auto text-yellow-600 mb-3" size={40} />
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">
            Employee Service Speeds are Disabled
          </h3>
          <p className="text-yellow-800">
            All employees will use standard service durations. Enable the toggle above to customize speeds per employee.
          </p>
        </div>
      ) : !sizingEnabled ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 font-medium">
            ⚠️ Size-based pricing is currently disabled. Service speeds by size are not applicable.
          </p>
          <p className="text-yellow-700 text-sm mt-2">
            To configure employee speeds for different sizes, enable size-based pricing in the Size Categories settings.
          </p>
        </div>
      ) : (
        <>
          <p className="text-gray-600 mb-6">
            Set how long each service takes for each category (in minutes). This affects appointment scheduling times.
            For example, if Jessica can do a Small Full Groom in 60 minutes but Ashley needs 90 minutes, set those durations here.
          </p>

          {groomers.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-600">No employees found. Add employees in the Users section first.</p>
            </div>
          ) : services.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <p className="text-gray-600">No services found. Add services in the Bookable Services section first.</p>
            </div>
          ) : sizeCategories.filter(cat => cat.is_active).length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 font-medium">
                ⚠️ No active size categories found.
              </p>
              <p className="text-yellow-700 text-sm mt-2">
                Add and activate size categories in the Size Categories settings to configure employee speeds.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {groomers.map(groomer => (
                <div key={groomer.id} className="border-2 border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <Users size={20} className="text-primary-600" />
                    <h3 className="text-lg font-bold">
                      {groomer.first_name} {groomer.last_name}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    {services.map(service => {
                      const activeSizes = sizeCategories.filter(cat => cat.is_active);
                      const matchingSizes = activeSizes.filter(cat => 
                        service.pricing?.some(p => 
                          p.size === cat.category_name || 
                          p.size.startsWith(cat.category_name + '_') ||
                          cat.category_name.startsWith(p.size + '_')
                        )
                      );
                      
                      if (matchingSizes.length === 0) {
                        return (
                          <div key={service.id} className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-semibold mb-2">{service.name}</h4>
                            <p className="text-sm text-yellow-600">
                              ⚠️ No matching size categories. Update service pricing to match current size categories.
                            </p>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={service.id} className="bg-gray-50 p-3 rounded-lg">
                          <h4 className="font-semibold mb-3">{service.name}</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {matchingSizes.map(sizeCategory => {
                              const size = sizeCategory.category_name;
                              // Find pricing with fuzzy match
                              const pricingEntry = service.pricing?.find(p => 
                                p.size === size || 
                                p.size.startsWith(size + '_') ||
                                size.startsWith(p.size + '_')
                              );
                              const baseDuration = pricingEntry?.durationMinutes;
                          const multiplier = getSpeed(groomer.id, service.id, size);
                          const actualDuration = baseDuration ? Math.round(baseDuration * multiplier) : 60;
                          
                          return (
                            <div key={size} className="bg-white p-3 rounded border border-gray-200">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                {sizeCategory.category_label}
                              </label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="number"
                              step="1"
                              min="1"
                              max="1440"
                              value={actualDuration}
                              onChange={(e) => {
                                const newDuration = parseInt(e.target.value);
                                if (!isNaN(newDuration) && baseDuration) {
                                  const newMultiplier = (newDuration / baseDuration).toFixed(2);
                                  console.log(`Input changed for ${groomer.first_name} - ${service.name} - ${size}:`, {
                                    newDuration,
                                    baseDuration,
                                    newMultiplier,
                                    groomerId: groomer.id,
                                    serviceId: service.id
                                  });
                                  handleSpeedChange(groomer.id, service.id, size, newMultiplier);
                                }
                              }}
                              className="input text-sm py-1 w-20"
                              placeholder="60"
                            />
                            <span className="text-xs text-gray-600">min</span>
                          </div>
                          {baseDuration && baseDuration !== actualDuration && (
                            <div className="mt-1 text-xs text-gray-500">
                              Base: {baseDuration}min
                            </div>
                          )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Set the actual time (in minutes) each employee needs for each service/size combination</li>
              <li>• Example: Small Full Groom - Jessica: 60 min, Ashley: 90 min</li>
              <li>• The system uses these durations to calculate appointment slots and prevent overbooking</li>
              <li>• If an employee is faster or slower, adjust the minutes accordingly</li>
              <li>• "Base" shows the default service duration for reference</li>
            </ul>
          </div>

          <div className="flex justify-end mt-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Save size={18} />
              <span>{saving ? 'Saving...' : 'Save Groomer Speeds'}</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GroomerSpeedManager;
